import { supabaseClient } from '../../services/supabase';
import { supabaseLocationService } from '../../services/supabaseLocationService';
import { SOLAR_COST_SERIES_START_YEAR } from '../../domains/narrative/solarCostStatistics';

interface SolarPVBatteryCostData {
  chartData: any;
  chartOptions: any;
  narrative?: string;
  coverageUnavailable?: boolean;
  comparisonYear?: number;
  latestSolarOnlyCostPerKw?: number | null;
  latestSolarStorageCostPerKw?: number | null;
  /** @deprecated use latestSolarOnlyCostPerKw */
  avgPVCost?: number | null;
  /** @deprecated use latestSolarStorageCostPerKw */
  avgBatteryCost?: number | null;
}

async function getGeofipsForLocation(locationName: string): Promise<number | null> {
  try {
    return await supabaseLocationService.getGeofips(locationName);
  } catch (error) {
    console.error('Error getting geofips for location:', locationName, error);
    return null;
  }
}

function emptyBatteryCost(location: string, reason: string): SolarPVBatteryCostData {
  return {
    coverageUnavailable: true,
    chartData: { labels: [], datasets: [] },
    chartOptions: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        title: {
          display: true,
          text: `Solar-only vs solar + storage (residential) — ${location}`,
        },
      },
    },
    narrative: reason,
    comparisonYear: undefined,
    latestSolarOnlyCostPerKw: null,
    latestSolarStorageCostPerKw: null,
    avgPVCost: null,
    avgBatteryCost: null,
  };
}

export default async function fetchSolarPVBatteryCostSupabase(
  location: string
): Promise<SolarPVBatteryCostData> {
  try {
    const geofips = await getGeofipsForLocation(location);

    if (!geofips) {
      return emptyBatteryCost(location, `No geofips found for ${location}.`);
    }

    let { data: techData, error } = await supabaseClient
      .from('tech_type_cost')
      .select('*')
      .eq('geoid', geofips)
      .order('year', { ascending: true });

    if (error) {
      console.error('Error fetching Solar PV Battery Cost data:', error);
      throw error;
    }

    if (!techData || techData.length === 0) {
      const { data: byLocation, error: locErr } = await supabaseClient
        .from('tech_type_cost')
        .select('*')
        .ilike('location_name', `%${location}%`)
        .order('year', { ascending: true });
      if (!locErr && byLocation && byLocation.length > 0) {
        techData = byLocation;
      }
    }

    if (!techData || techData.length === 0) {
      return emptyBatteryCost(location, `No tech_type_cost records for ${location}.`);
    }

    const validTechData = techData;
    const allYears = Array.from(new Set(validTechData.map((item) => Number(item.year)))).sort(
      (a, b) => a - b
    );
    const years = allYears.filter((year) => year >= SOLAR_COST_SERIES_START_YEAR && year <= 2025);

    const pvData = years.map((year) => {
      const yearData = validTechData.find((item) => Number(item.year) === year);
      if (!yearData || !yearData.photovoltaic) return null;
      const v = Number(yearData.photovoltaic);
      return Number.isFinite(v) && v > 0 ? v : null;
    });

    const batteryData = years.map((year) => {
      const yearData = validTechData.find((item) => Number(item.year) === year);
      if (!yearData || !yearData.photovoltaic_battery_energy_storage) return null;
      const v = Number(yearData.photovoltaic_battery_energy_storage);
      return Number.isFinite(v) && v > 0 ? v : null;
    });

    const chartData = {
      labels: years.map(String),
      datasets: [
        {
          label: 'Solar-only (mean $/kW)',
          data: pvData,
          borderColor: '#3b82f6',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          fill: false,
          tension: 0.1,
          spanGaps: false,
        },
        {
          label: 'Solar + storage (mean $/kW)',
          data: batteryData,
          borderColor: '#10b981',
          backgroundColor: 'rgba(16, 185, 129, 0.1)',
          fill: false,
          tension: 0.1,
          spanGaps: false,
        },
      ],
    };

    const chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        title: {
          display: true,
          text: `Residential mean reported cost by technology group — ${location}`,
          font: { size: 16, weight: 'bold' as const },
        },
        legend: { position: 'top' as const },
      },
      scales: {
        x: { title: { display: true, text: 'Year' } },
        y: {
          title: { display: true, text: 'Nominal mean reported cost ($/kW)' },
          beginAtZero: true,
        },
      },
    };

    let comparisonYear: number | undefined;
    let latestSolarOnlyCostPerKw: number | null = null;
    let latestSolarStorageCostPerKw: number | null = null;
    for (let i = years.length - 1; i >= 0; i--) {
      const y = years[i];
      const pv = pvData[i];
      const bat = batteryData[i];
      if (pv != null || bat != null) {
        comparisonYear = y;
        latestSolarOnlyCostPerKw = pv;
        latestSolarStorageCostPerKw = bat;
        break;
      }
    }

    const narrative =
      'Residential technology groups from DGStats-derived tech_type_cost (mean $/kW DC per group). Not the same metric as aggregate $/W DC in the Cost view. Difference is descriptive, not battery cost.';

    return {
      chartData,
      chartOptions,
      narrative,
      coverageUnavailable: false,
      comparisonYear,
      latestSolarOnlyCostPerKw,
      latestSolarStorageCostPerKw,
      avgPVCost: latestSolarOnlyCostPerKw,
      avgBatteryCost: latestSolarStorageCostPerKw,
    };
  } catch (error) {
    console.error('Error in fetchSolarPVBatteryCostSupabase:', error);
    throw error;
  }
}
